import { z } from 'zod';
import {
  MAP_TILES_X,
  MAP_TILES_Y,
  MAX_BODY_LENGTH,
  MAX_NICKNAME_LENGTH,
  MAX_ROOM_NAME_LENGTH,
  REACTION_EMOJIS,
} from './limits.js';

// ─── 식별자 ─────────────────────────────────────────────────

export const userIdSchema = z.string().min(1).max(64);
export const roomIdSchema = z.string().min(1).max(64);
export const messageIdSchema = z.uuid();

/**
 * 초대코드. Crockford Base32(I·L·O·U 제외) 8자 ≈ 40비트.
 * 예: SOKDAQ-7F3K-92QX
 */
export const INVITE_CODE_PATTERN = /^SOKDAQ-[0-9A-HJKMNP-TV-Z]{4}-[0-9A-HJKMNP-TV-Z]{4}$/;
export const inviteCodeSchema = z.string().regex(INVITE_CODE_PATTERN, {
  message: '초대코드 형식이 올바르지 않습니다 (예: SOKDAQ-7F3K-92QX)',
});

/** HMAC 서명된 재접속 토큰. 서버만 발급·검증한다. */
export const memberTokenSchema = z.string().min(1).max(512);

// ─── 값 타입 ────────────────────────────────────────────────

export const timestampSchema = z.number().int().nonnegative();
export const seqSchema = z.number().int().nonnegative();

export const nicknameSchema = z.string().trim().min(1).max(MAX_NICKNAME_LENGTH);
export const roomNameSchema = z.string().trim().min(1).max(MAX_ROOM_NAME_LENGTH);
export const bodySchema = z.string().min(1).max(MAX_BODY_LENGTH);

const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);

export const facingSchema = z.enum(['up', 'down', 'left', 'right']);
export type Facing = z.infer<typeof facingSchema>;

export const messageKindSchema = z.enum(['text', 'emote', 'whisper', 'system']);
export type MessageKind = z.infer<typeof messageKindSchema>;

export const roleSchema = z.enum(['owner', 'member']);
export type Role = z.infer<typeof roleSchema>;

/** 뷰별 스킨 식별자. 서버는 저장만 하고 해석하지 않는다. */
export const themeSchema = z.enum(['office', 'stealth', 'classic']);
export type Theme = z.infer<typeof themeSchema>;

// ─── 좌표 ──────────────────────────────────────────────────

/** 타일 단위 좌표. 정수가 아니어도 된다(보간 중간값). */
export const positionSchema = z.object({
  x: z.number().min(0).max(MAP_TILES_X),
  y: z.number().min(0).max(MAP_TILES_Y),
  facing: facingSchema,
});
export type Position = z.infer<typeof positionSchema>;

// ─── 아바타 ─────────────────────────────────────────────────

export const avatarSchema = z.object({
  sprite: z.string().min(1).max(32),
  skin: hexColorSchema,
  hair: hexColorSchema,
  top: hexColorSchema,
});
export type Avatar = z.infer<typeof avatarSchema>;

// ─── 엔티티 ─────────────────────────────────────────────────

export const memberSchema = z.object({
  userId: userIdSchema,
  nickname: nicknameSchema,
  avatar: avatarSchema,
  role: roleSchema,
  joinedAt: timestampSchema,
  lastSeenAt: timestampSchema,
  online: z.boolean(),
  x: z.number(),
  y: z.number(),
  facing: facingSchema,
});
export type Member = z.infer<typeof memberSchema>;

export const chatMessageSchema = z.object({
  /** 방 안에서 단조 증가. 재접속 델타 동기화의 기준점. */
  seq: seqSchema,
  /** 클라이언트가 생성한 UUID. 중복 전송을 서버가 걸러내는 데 쓴다. */
  id: messageIdSchema,
  userId: userIdSchema,
  kind: messageKindSchema,
  body: z.string(),
  /** whisper/zone 메시지의 가시 범위. 전체 공개면 null. */
  zone: z.string().max(32).nullable(),
  createdAt: timestampSchema,
  editedAt: timestampSchema.nullable(),
  deletedAt: timestampSchema.nullable(),
});
export type ChatMessage = z.infer<typeof chatMessageSchema>;

export const roomInfoSchema = z.object({
  id: roomIdSchema,
  name: roomNameSchema,
  theme: themeSchema,
  ownerId: userIdSchema,
  createdAt: timestampSchema,
  /** true면 신규 입장이 막힌다. */
  locked: z.boolean(),
});
export type RoomInfo = z.infer<typeof roomInfoSchema>;

/**
 * 리액션 이모지.
 *
 * 목록에 있는 것만 받는다. 임의 문자열을 통과시키면 남의 화면에 아무
 * 텍스트나 띄울 수 있다.
 */
export const reactionEmojiSchema = z.enum(REACTION_EMOJIS);

export const reactionSchema = z.object({
  msgId: messageIdSchema,
  userId: userIdSchema,
  emoji: reactionEmojiSchema,
});
export type Reaction = z.infer<typeof reactionSchema>;
